import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import Navbar from '../components/Navbar';
import MobileNavbar from '../components/MobileNavbar';
import { updateUploadSettings } from '../services/api';
import { useUploadSettingsStore } from '../stores/uploadSettingsStore';
import { Save, RotateCcw, Info } from 'lucide-react';

const DEFAULTS = { maxDimension: 1600, jpegQuality: 0.8, maxSizeKb: 900 };

const SettingsPage = () => {
  const { settings, load, setSettings } = useUploadSettingsStore();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setForm(settings); }, [settings]);

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleReset = () => setForm(DEFAULTS);

  const handleSave = async () => {
    const { maxDimension, jpegQuality, maxSizeKb } = form;
    if (maxDimension < 400 || maxDimension > 4096) return toast.error('Max dimension must be between 400 and 4096 px');
    if (jpegQuality < 0.4 || jpegQuality > 0.95) return toast.error('JPEG quality must be between 0.4 and 0.95');
    if (maxSizeKb < 100 || maxSizeKb > 4096) return toast.error('Max size must be between 100 and 4096 KB');

    setSaving(true);
    try {
      const res = await updateUploadSettings({
        maxDimension: Math.round(maxDimension),
        jpegQuality: Number(jpegQuality),
        maxSizeKb: Math.round(maxSizeKb),
      });
      setSettings(res.data);
      toast.success('Upload settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <MobileNavbar />
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="settings-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Control how uploaded images and documents are compressed before they reach the server.</p>
        </div>

        <Card className="p-6 space-y-6" data-testid="upload-settings-card">
          <div>
            <h2 className="font-semibold text-base text-slate-800">Upload &amp; Compression</h2>
            <p className="text-xs text-slate-500 mt-1">
              Applies to every image upload in the portal (verification screenshots, KYC, cards, UPI QR, company logo).
            </p>
          </div>

          {/* Max Dimension */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Max image dimension</Label>
              <span className="text-sm font-semibold text-blue-700">{form.maxDimension} px</span>
            </div>
            <Slider
              data-testid="max-dimension-slider"
              min={400} max={4096} step={100}
              value={[form.maxDimension]}
              onValueChange={([v]) => update('maxDimension', v)}
            />
            <p className="text-xs text-slate-500">Long edge. Photos larger than this are scaled down proportionally. Higher = sharper scans, bigger payloads.</p>
          </div>

          {/* JPEG Quality */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">JPEG quality</Label>
              <span className="text-sm font-semibold text-blue-700">{Math.round(form.jpegQuality * 100)}%</span>
            </div>
            <Slider
              data-testid="jpeg-quality-slider"
              min={0.4} max={0.95} step={0.05}
              value={[form.jpegQuality]}
              onValueChange={([v]) => update('jpegQuality', v)}
            />
            <p className="text-xs text-slate-500">Starting quality for JPEG encoding. Automatically lowered further if the image still exceeds the size cap.</p>
          </div>

          {/* Max Size */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Max payload size (KB)</Label>
            <Input
              data-testid="max-size-input"
              type="number" min={100} max={4096}
              value={form.maxSizeKb}
              onChange={(e) => update('maxSizeKb', Number(e.target.value) || 0)}
              className="w-40"
            />
            <p className="text-xs text-slate-500">Hard cap on the final base64 size. Keep under your infrastructure&apos;s ingress body limit (typically 1024 KB on most cloud hosts).</p>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2 text-xs text-amber-900">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Changes apply immediately to everyone in your company on their next upload. Existing uploads are not re-processed.</span>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t">
            <Button data-testid="save-upload-settings-btn" onClick={handleSave} disabled={saving} className="gap-2">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
            </Button>
            <Button data-testid="reset-upload-settings-btn" onClick={handleReset} variant="outline" className="gap-2">
              <RotateCcw className="w-4 h-4" /> Reset to defaults
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
